import type { Ref } from 'react';
import { useCallback, useEffect, useId } from 'react';
import FreeTextInput from '../FreeTextInput/FreeTextInput.component';
import useGoogleAddressAutocomplete from 'hooks/shared/useGoogleAddressAutocomplete';
import type AddressAutocompleteInputProps from './AddressAutocompleteInput.types';
import AddressSearchFieldSuggestions, {
  getAddressSuggestionOptionId,
} from '../AddressSearchField/AddressSearchFieldSuggestions.component';

/**
 * Drop-in replacement for FreeTextInput with Google Places Autocomplete.
 * Uses the Places API (New) REST endpoint — no Maps JavaScript SDK required.
 * Falls back gracefully to a plain input when the API key is absent.
 */
const AddressAutocompleteInput = ({
  ref,
  onAddressSelect,
  countryRestrictions,
  includedPrimaryTypes,
  locationHint,
  onChange,
  onBlur,
  ...props
}: AddressAutocompleteInputProps & { ref?: Ref<HTMLInputElement> }) => {
  const listboxId = useId();

  const {
    suggestions,
    isOpen,
    activeIndex,
    handleInputChange,
    handleSuggestionSelect,
    handleKeyDown,
    closeDropdown,
  } = useGoogleAddressAutocomplete({
    onPlaceSelect: onAddressSelect,
    countryRestrictions,
    includedPrimaryTypes,
    locationHint,
  });

  const expanded = isOpen && suggestions.length > 0;

  useEffect(() => {
    if (activeIndex < 0) return;
    document
      .getElementById(getAddressSuggestionOptionId(listboxId, activeIndex))
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, listboxId]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (onChange as any)?.(e);
      handleInputChange(e.target.value);
    },
    [onChange, handleInputChange],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Delay so a mousedown on a suggestion fires before the dropdown closes.
      setTimeout(closeDropdown, 150);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (onBlur as any)?.(e);
    },
    [onBlur, closeDropdown],
  );

  const pickSuggestion = useCallback(
    (suggestion: Parameters<typeof handleSuggestionSelect>[0]) => {
      void handleSuggestionSelect(suggestion);
    },
    [handleSuggestionSelect],
  );

  return (
    <div className="relative">
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <FreeTextInput
        {...props}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={expanded ? listboxId : undefined}
        aria-activedescendant={
          expanded && activeIndex >= 0
            ? getAddressSuggestionOptionId(listboxId, activeIndex)
            : undefined
        }
        onChange={handleChange as any}
        onBlur={handleBlur as any}
        onKeyDown={handleKeyDown as any}
        ref={ref as any}
      />

      {expanded && (
        <AddressSearchFieldSuggestions
          listboxId={listboxId}
          suggestions={suggestions}
          activeIndex={activeIndex}
          onPick={pickSuggestion}
        />
      )}

      {/* Visually hidden live region — announces suggestion count to screen readers */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {expanded
          ? `${suggestions.length} suggestion${
              suggestions.length === 1 ? '' : 's'
            } available`
          : ''}
      </div>
    </div>
  );
};

AddressAutocompleteInput.displayName = 'AddressAutocompleteInput';

export default AddressAutocompleteInput;
